import React, { useId } from "react";
import { Box } from "@mui/material";

const transicaoDeCor = {
  background: "linear-gradient(-45deg, #0035b9, #0060ef, #70c4ff, #d1f8ff)",
  backgroundSize: "400% 400%",
  animation: "sear-transicao-de-cor 15s ease infinite",

  "@keyframes sear-transicao-de-cor": {
    "0%, 100%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
  },

  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    backgroundPosition: "50% 50%",
  },
};

function OndasAcesso({ claro = false }) {
  const id = useId();

  const cores = claro
    ? {
        fundo: ["#0878f4", "#45aaff", "#d1f8ff"],
        onda: ["#0069ed", "#168cff", "#70ceff"],
        faixa: ["#258fff", "#79ceff", "#eeffff"],
        brilho: 0.5,
        linha: 0.4,
      }
    : {
        fundo: ["#0035b9", "#0054df", "#59b6ff"],
        onda: ["#001e85", "#0040c6", "#087cf5"],
        faixa: ["#003bc4", "#167cf4", "#70c4ff"],
        brilho: 0.25,
        linha: 0.3,
      };

  return (
    <svg
      viewBox="0 0 1440 1080"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      style={{
        display: "block",
        width: "100%",
        height: "100%",
      }}
    >
      <defs>
        <radialGradient id={`${id}-fundo`} cx="50%" cy="35%" r="85%">
          <stop offset="0%" stopColor={cores.fundo[0]} />
          <stop offset="52%" stopColor={cores.fundo[1]} />
          <stop offset="100%" stopColor={cores.fundo[2]} />
        </radialGradient>

        <linearGradient id={`${id}-onda`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={cores.onda[0]} />
          <stop offset="55%" stopColor={cores.onda[1]} />
          <stop offset="100%" stopColor={cores.onda[2]} />
        </linearGradient>

        <linearGradient id={`${id}-faixa`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={cores.faixa[0]} />
          <stop offset="58%" stopColor={cores.faixa[1]} />
          <stop offset="100%" stopColor={cores.faixa[2]} />
        </linearGradient>

        <radialGradient id={`${id}-brilho`} cx="0" cy="0" r="0.7">
          <stop offset="0%" stopColor="#e8ffff" stopOpacity={cores.brilho} />
          <stop offset="100%" stopColor="#e8ffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="1440" height="1080" fill={`url(#${id}-fundo)`} />

      <rect width="1440" height="1080" fill={`url(#${id}-brilho)`} />

      <path
        d="M 0 105 C 230 265, 390 555, 660 640 C 955 735, 1190 335, 1440 355 L 1440 1080 H 0 Z"
        fill={`url(#${id}-onda)`}
        opacity="0.72"
      />

      <path
        d="M 0 285 C 265 335, 460 705, 690 875 C 955 1070, 1165 655, 1440 505 L 1440 1080 H 0 Z"
        fill={`url(#${id}-faixa)`}
      />

      <path
        d="M 0 440 C 250 440, 445 845, 660 960 C 960 1120, 1155 755, 1440 640 L 1440 1080 H 0 Z"
        fill={`url(#${id}-onda)`}
        opacity="0.75"
      />

      <path
        d="M 0 585 C 250 590, 420 855, 407 1080 H 0 Z"
        fill={`url(#${id}-faixa)`}
      />

      <path
        d="M 0 750 C 145 785, 225 945, 208 1080 H 0 Z"
        fill={`url(#${id}-onda)`}
        opacity="0.65"
      />

      <path
        d="M 1100 0 C 1145 160, 1285 250, 1440 257 V 0 Z"
        fill={`url(#${id}-faixa)`}
        opacity="0.85"
      />

      <g fill="none" stroke="#ffffff" strokeWidth="1.2" opacity={cores.linha}>
        <path d="M 0 285 C 265 335, 460 705, 690 875 C 955 1070, 1165 655, 1440 505" />

        <path d="M 0 440 C 250 440, 445 845, 660 960 C 960 1120, 1155 755, 1440 640" />

        <path d="M 0 585 C 250 590, 420 855, 407 1080" />

        <path d="M 1100 0 C 1145 160, 1285 250, 1440 257" />

        <path d="M 1440 115 C 1300 390, 1090 480, 860 520" />
      </g>
    </svg>
  );
}

export default function IdentidadeVisual({
  children,
  component = "div",
  escuro = false,
  variante = "padrao",
  suave = false,
  sx = {},
}) {
  if (variante === "acesso") {
    return (
      <Box
        component={component}
        sx={{
          position: "relative",
          isolation: "isolate",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",

          ...transicaoDeCor,

          background: suave
            ? "linear-gradient(-45deg, #0878f4, #3295fa, #70c4ff, #d1f8ff)"
            : transicaoDeCor.background,

          WebkitFontSmoothing: suave ? "antialiased" : undefined,
          MozOsxFontSmoothing: suave ? "grayscale" : undefined,

          px: 2,
          py: { xs: 4, sm: 6 },

          ...sx,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: suave ? 0.22 : 0.3,
          }}
        >
          <OndasAcesso claro={suave} />
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",

            "& .MuiTextField-root .MuiOutlinedInput-root": {
              fontSize: suave ? "14px" : "16px",
            },

            "& .MuiTextField-root .MuiOutlinedInput-input": {
              fontSize: suave ? "13px" : "inherit",
              lineHeight: 1.25,
              height: "auto",
              paddingTop: "7px",
              paddingBottom: "7px",
            },

            "& .MuiTextField-root .MuiInputLabel-root": {
              fontSize: suave ? "14px" : "16px",
            },

            "& .MuiTextField-root .MuiFormHelperText-root": {
              fontSize: suave ? "11px" : "13px",
              lineHeight: 1.3,
            },

            "& h1.MuiTypography-root": {
              fontSize: suave ? "22px" : "24px",
              lineHeight: 1.2,
            },

            "& p.MuiTypography-root": {
              fontSize: suave ? "12px" : "14px",
              lineHeight: suave ? 1.3 : 1.35,
            },

            "& span.MuiTypography-root": {
              fontSize: suave ? "11px" : "13px",
              lineHeight: 1.3,
            },

            "& .MuiButton-root": {
              fontSize: suave ? "14px" : "16px",
              lineHeight: suave ? 1.2 : 1.3,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  const rodape = component === "footer";

  const fundo = escuro
    ? "linear-gradient(115deg, #102c4c 0%, #174773 52%, #12375b 100%)"
    : rodape
      ? "linear-gradient(110deg, #0086ff 0%, #24a0fa 55%, #008aff 100%)"
      : "linear-gradient(110deg, #008bff 0%, #0798ff 48%, #62bcfa 100%)";

  return (
    <Box
      component={component}
      sx={{
        position: "relative",
        isolation: "isolate",
        overflow: "hidden",
        color: "#ffffff",
        background: fundo,
        ...sx,
      }}
    >
      <svg
        viewBox="0 0 1440 180"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {rodape ? (
          <>
            <path
              d="M 0 0 C 250 0, 370 40, 620 98 C 910 165, 1120 15, 1440 60 V 0 Z"
              fill="#75caff"
              opacity={escuro ? 0.08 : 0.22}
            />

            <path
              d="M 0 36 C 180 150, 380 126, 610 180 H 0 Z"
              fill="#006ce6"
              opacity={escuro ? 0.12 : 0.25}
            />

            <path
              d="M 0 180 C 330 75, 580 90, 820 158 C 1080 230, 1240 100, 1440 130 V 180 Z"
              fill="#5bbfff"
              opacity={escuro ? 0.08 : 0.24}
            />

            <path
              d="M 920 0 C 1100 5, 1250 5, 1440 52 V 0 Z"
              fill="#0071ec"
              opacity={escuro ? 0.12 : 0.2}
            />
          </>
        ) : (
          <>
            <path
              d="M 410 0 C 590 30, 670 150, 850 180 H 1110 C 930 98, 820 35, 740 0 Z"
              fill="#0075ed"
              opacity={escuro ? 0.12 : 0.2}
            />

            <path
              d="M 550 180 C 720 125, 760 38, 900 0 H 1180 C 1040 85, 960 145, 870 180 Z"
              fill="#7bd0ff"
              opacity={escuro ? 0.08 : 0.27}
            />

            <path
              d="M 820 180 C 910 180, 890 0, 1010 0 H 1440 V 180 Z"
              fill={escuro ? "#1c4265" : "#e1effd"}
            />

            <path
              d="M 1110 180 C 1180 108, 1270 70, 1440 90 V 180 Z"
              fill={escuro ? "#244e73" : "#d6eafb"}
              opacity="0.5"
            />
          </>
        )}
      </svg>

      <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
    </Box>
  );
}
